import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api, { ensureCsrfToken, clearCsrfToken } from '../api/client'

export const useAuthStore = defineStore('auth', () => {
  const user = ref(null)
  const token = ref(null)
  const isLoading = ref(false)
  const isInitialized = ref(false)
  const pendingLegal = ref([])
  const pendingServiceContract = ref(false)

  const isAuthenticated = computed(() => {
    return !!user.value && isInitialized.value
  })

  const hasPendingLegal = computed(() => pendingLegal.value.length > 0)

  const setPendingLegal = (docs) => {
    pendingLegal.value = Array.isArray(docs) ? docs : []
  }

  const clearPendingLegal = () => {
    pendingLegal.value = []
  }

  const setPendingServiceContract = (pending) => {
    pendingServiceContract.value = !!pending
  }

  const clearPendingServiceContract = () => {
    pendingServiceContract.value = false
  }

  const setToken = (newToken) => {
    token.value = newToken || null
  }

  // Limpeza de cookies stale em andamento (iniciada ao abrir /login, sem
  // bloquear a tela). O login espera ela terminar: senão um logout atrasado
  // (servidor frio) chegaria depois do login e apagaria o cookie novo.
  let staleCleanupPromise = null

  const login = async (credentials) => {
    try {
      isLoading.value = true
      if (staleCleanupPromise) await staleCleanupPromise
      const response = await api.post('/auth/login', credentials)
      
      const { user: userData } = response.data
      
      setToken(null)
      user.value = userData
      setPendingLegal(response.data.pending_legal)
      setPendingServiceContract(response.data.pending_service_contract)

      return { success: true, user: userData }
    } catch (error) {
      console.error('Login error:', error)
      return { 
        success: false, 
        message: error.response?.data?.error || 'Erro no login' 
      }
    } finally {
      isLoading.value = false
    }
  }

  const register = async (userData) => {
    try {
      isLoading.value = true
      const response = await api.post('/auth/register', userData)
      
      return { success: true, user: response.data.user }
    } catch (error) {
      console.error('Register error:', error)
      return { 
        success: false, 
        message: error.response?.data?.error || 'Erro no cadastro' 
      }
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    try {
      await api.post('/auth/logout')
    } catch (error) {
      console.warn('Logout backend failed:', error)
    }

    clearCsrfToken()
    setToken(null)
    user.value = null
    pendingLegal.value = []
    pendingServiceContract.value = false
    isInitialized.value = true
  }

  const clearAuth = () => {
    token.value = null
    user.value = null
    pendingLegal.value = []
    pendingServiceContract.value = false
  }

  // Limpa cookies httpOnly stale (auth_token / _csrf) ao abrir a tela de login.
  // Evita validate 401 ruidoso e conflitos quando a aba ficou horas aberta.
  // Nunca rejeita e é deduplicada: chamadas concorrentes compartilham a mesma
  // execução (1 csrf-token + 1 logout, em vez de um par por chamador).
  const clearStaleSessionCookies = () => {
    if (!staleCleanupPromise) {
      staleCleanupPromise = (async () => {
        try {
          await ensureCsrfToken()
          await api.post('/auth/logout')
        } catch (error) {
          console.warn('clearStaleSessionCookies failed:', error)
        } finally {
          clearCsrfToken()
          staleCleanupPromise = null
        }
      })()
    }
    return staleCleanupPromise
  }

  let checkAuthPromise = null

  // App.vue (onMounted) e o router.beforeEach chamam checkAuth ao mesmo tempo
  // no primeiro load — compartilham a mesma execução.
  const checkAuth = () => {
    if (!checkAuthPromise) {
      checkAuthPromise = runCheckAuth().finally(() => {
        checkAuthPromise = null
      })
    }
    return checkAuthPromise
  }

  const runCheckAuth = async () => {
    try {
      isLoading.value = true

      const onLoginPage =
        typeof window !== 'undefined' &&
        (window.location.pathname === '/login' || window.location.pathname.endsWith('/login'))

      if (onLoginPage) {
        // Não espera a limpeza: a tela de login aparece na hora, mesmo com o
        // backend frio. login() aguarda a limpeza antes de enviar credenciais.
        clearStaleSessionCookies()
        clearAuth()
        return
      }

      ensureCsrfToken().catch(() => {})

      const response = await api.get('/auth/validate')
      if (response.data && response.data.user) {
        user.value = response.data.user
        setPendingLegal(response.data.pending_legal)
        setPendingServiceContract(response.data.pending_service_contract)
      } else {
        clearAuth()
      }
    } catch (error) {
      console.log('Sessão inválida detectada - auto-logout')
      clearAuth()
    } finally {
      isLoading.value = false
      isInitialized.value = true
    }
  }

  const refreshToken = async () => {
    try {
      await api.post('/auth/refresh')
      return true
    } catch (error) {
      console.error('Token refresh error:', error)
      await logout()
      return false
    }
  }

  const mustChangePassword = computed(() => !!user.value?.must_change_password)

  // Tenant sem acesso liberado — assinatura cancelada ou plano vinculado
  // pelo platform owner que ainda não teve o primeiro pagamento confirmado
  // (ver subscription_status em platform.js/stripeWebhook.js no backend).
  // Acesso fica restrito a Configurações > Pagamentos/Legal (ver Navbar.vue
  // e router/index.js). Platform owner não tem tenant/assinatura, nunca é afetado,
  // e tenant interno (is_internal) dispensa assinatura — espelha
  // isSubscriptionBlocked em tenantScope.js no backend.
  const BLOCKED_SUBSCRIPTION_STATUSES = new Set(['canceled', 'pending_payment'])
  const subscriptionBlocked = computed(() =>
    user.value?.role !== 'PLATFORM_OWNER'
      && !user.value?.is_internal
      && BLOCKED_SUBSCRIPTION_STATUSES.has(user.value?.subscription_status)
  )

  const clearMustChangePassword = () => {
    if (user.value) user.value = { ...user.value, must_change_password: false }
  }

  // Funcionalidades habilitadas pelo plano do tenant (marcadas em
  // PlatformPlans.vue a partir do catálogo de módulos, ver backend
  // src/utils/tenantFeatures.js). feature_keys vem null quando o tenant não
  // tem plano vinculado — mantém acesso total (mesmo comportamento de antes
  // dessa feature existir). Platform owner também nunca é restringido.
  const hasFeature = (key) => {
    // CLIENT_SUBSCRIPTIONS tem uma trava extra por env var no backend (ver
    // FEATURE_CLIENT_SUBSCRIPTIONS/CLIENT_SUBSCRIPTIONS_ENABLED em app.js) —
    // enquanto nenhum tenant tem plano vinculado, feature_keys vem null (sem
    // restrição) pra tudo, o que sozinho mostraria essa aba pra todo mundo
    // mesmo com a rota desligada no servidor. Checa a env var primeiro, só
    // pra essa chave.
    if (key === 'CLIENT_SUBSCRIPTIONS' && !user.value?.client_subscriptions_enabled) return false
    const keys = user.value?.feature_keys
    return keys == null || keys.includes(key)
  }

  // Para itens que agrupam várias sub-funcionalidades (ex: menu
  // Relacionamento = Cupons + Fidelidade + Comissões) — libera se pelo
  // menos uma das chaves estiver no plano.
  const hasAnyFeature = (keys) => keys.some((key) => hasFeature(key))

  // Permissões do PROFISSIONAL, configuradas pelo admin em Configurações >
  // Permissões (backend src/utils/permissions.js). Só o perfil PROFISSIONAL
  // vem com `permissions` (um mapa chave -> boolean); ADMIN e RECEPTIONIST
  // seguem sem restrição. Sem o mapa carregado, o profissional é tratado como
  // sem permissão (falha fechada). A API é quem de fato barra — isto só
  // decide o que mostrar na tela.
  const can = (key) => {
    if (user.value?.role !== 'PROFESSIONAL') return true
    return Boolean(user.value?.permissions?.[key])
  }

  return {
    user,
    token,
    isLoading,
    isInitialized,
    isAuthenticated,
    mustChangePassword,
    subscriptionBlocked,
    hasFeature,
    hasAnyFeature,
    can,
    pendingLegal,
    hasPendingLegal,
    setPendingLegal,
    clearPendingLegal,
    pendingServiceContract,
    setPendingServiceContract,
    clearPendingServiceContract,
    login,
    register,
    logout,
    clearAuth,
    clearStaleSessionCookies,
    checkAuth,
    refreshToken,
    setToken,
    clearMustChangePassword,
  }
})